(() => {
  'use strict';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  // Look filters shown on steps 2–4: [tagValue, label]
  const LOOKS = [
    ['day', 'Day'],
    ['night', 'Night'],
    ['celebration', 'Celebration'],
  ];

  // "None" swatch label per step (steps 2–4) — shown as the hover tooltip
  const NONE_LABELS = {
    step2: 'No Deco',
    step3: 'No Tassel',
    step4: 'No Strap',
  };

  // Matching deco/strap PNGs are exported full-bleed to the base image's own
  // canvas, so they always sit flush over it — no per-product position needed.
  const MATCHING_OVERLAY_POS = { top: '0%', left: '50%', scale: 100 };

  // Running discount by number of paid add-ons (steps 2–4) on top of the bag.
  // Index = add-on count: bag alone 0%, +1 → 10%, +2 → 15%, +3 → 20%.
  // The store must configure a matching Shopify automatic discount so the cart
  // charges these same rates — the visualiser only mirrors it visually.
  const DISCOUNT_TIERS = [0, 0.1, 0.15, 0.2];

  // Free-gift mode (rewardType 'free_gift'): cumulative gift unlocks by add-on
  // count. Tier index i is unlocked once addonCount >= threshold[i]. A store
  // configured for gift mode ignores DISCOUNT_TIERS entirely.
  const GIFT_TIER_THRESHOLDS = [1, 2];

  // Classic Shopify money formatter — supports the {{amount}} placeholder family
  function formatMoney(cents, format) {
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const fmt = format || '${{amount}}';
    let out = '';

    function withCommas(number, precision, thousands, decimal) {
      const dp = precision == null ? 2 : precision;
      const ts = thousands == null ? ',' : thousands;
      const ds = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      const num = (number / 100).toFixed(dp);
      const parts = num.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${ts}`);
      const centsPart = parts[1] ? ds + parts[1] : '';
      return dollars + centsPart;
    }

    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    switch (placeholder) {
      case 'amount':
        out = withCommas(value, 2);
        break;
      case 'amount_no_decimals':
        out = withCommas(value, 0);
        break;
      case 'amount_with_comma_separator':
        out = withCommas(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withCommas(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withCommas(value, 2, "'", '.');
        break;
      default:
        out = withCommas(value, 2);
    }
    // Strip any HTML wrapper (e.g. shop.money_format's <span class="money">…</span>)
    // since the result is inserted as plain text.
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
  }

  // position string: "top,left,scale" where scale = multiplier (%), 100 = intrinsic uploaded size
  function parsePosition(str) {
    if (!str) return null;
    const [top, left, scale] = str.split(',').map(Number);
    const numericScale = Number.isFinite(scale) && scale > 0 ? scale : 100;
    return {
      top: `${top}%`,
      left: `${left}%`,
      scale: numericScale,
    };
  }

  // Steps 2–4 accessories with `universalHardware: true` (hardware not visible on
  // the product, e.g. Gold-only fixings on the back) show under either bag hardware.
  function filterByHardware(products, hardware) {
    return products.filter((p) => (p.hardware === hardware || p.universalHardware) && p.available);
  }

  function filterByLook(products, look) {
    return products.filter((p) => Array.isArray(p.looks) && p.looks.includes(look));
  }

  // Groups colour-variant products under their shared style/product name
  // (product.title has already had the " | Colour" suffix stripped in Liquid).
  function groupByStyle(products) {
    const map = new Map();
    for (const p of products) {
      if (!map.has(p.title)) map.set(p.title, []);
      map.get(p.title).push(p);
    }
    return map;
  }

  // Step-4 products are a mix of charms and straps; straps are identified by product type
  function isStrap(p) {
    return ['strap', 'chain'].includes((p.productType || '').toLowerCase());
  }

  // Bag / accessory size compatibility (Product Info metaobject `compatible` field).
  // A bag is 'Classic' or 'Mini'; an accessory is 'Classic', 'Mini' or 'Universal'
  // (fits either). Blank is treated as universal so nothing is hidden until sizes
  // are actually set in the metaobject — keeps the single-bag launch unaffected.
  function isCompatible(product, bagCompatible) {
    const pc = (product.compatible || '').trim().toLowerCase();
    if (!pc || pc === 'universal') return true;
    const bag = (bagCompatible || '').trim().toLowerCase();
    if (!bag) return true; // bag size unknown → don't filter
    return pc === bag;
  }

  function el(tag, attrs = {}, ...children) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (k === 'class') node.className = v;
      else if (k.startsWith('data-')) node.dataset[k.slice(5)] = v;
      else node.setAttribute(k, v);
    }
    for (const child of children) {
      if (child == null) continue;
      node.append(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
  }

  function labelRow(label, value) {
    return el(
      'div',
      { class: 'sh-visualiser-label-row' },
      el('span', { class: 'sh-visualiser-label-row__label' }, label),
      el('span', { class: 'sh-visualiser-label-row__value' }, value || '')
    );
  }

  // ─── Main controller ────────────────────────────────────────────────────────

  class ProductVisualiser {
    constructor(root) {
      this.root = root;
      this.config = this._parseConfig();
      if (!this.config) return;
      this.isGiftMode = this.config.rewardType === 'free_gift';
      this.isNoReward = this.config.rewardType === 'no_reward';
      this.isBasic = this.config.visualiserType === 'basic';
      this.chooseLookEnabled = this.config.chooseLook === true;

      this.heroEl = root.querySelector('.sh-product-visualiser__hero');
      this.controlsEl = root.querySelector('.sh-product-visualiser__controls');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.state = {
        hardware: 'Gold',
        addDecoStrap: true, // matching deco/strap shown by default (bare bag opt-out removed)
        selected: {
          step1: null, // bag colour, always set
          step2: null, // front, optional
          step3: null, // tassel, optional + gated
          step4: null, // charm or strap, optional
        },
        look: {
          step2: 'day',
          step3: 'day',
          step4: 'day',
        },
        // Selected style/product name within the current look (steps 2–4) —
        // colour swatches are scoped to whichever style is active here.
        style: {
          step2: null,
          step3: null,
          step4: null,
        },
      };

      this._init();
    }

    _parseConfig() {
      const scriptEl = this.root.querySelector('[data-product-visualiser-config]');
      if (!scriptEl) return null;
      try {
        return JSON.parse(scriptEl.textContent);
      } catch (e) {
        console.error('[ProductVisualiser] Failed to parse config JSON', e);
        return null;
      }
    }

    _init() {
      const { steps } = this.config;
      const hw = this.state.hardware;

      const s1 = filterByHardware(steps.step1 || [], hw);
      this.state.selected.step1 = s1[0] || null;
      // Steps 2–4 start unselected — hero defaults to the bare bag.

      this._preloadBaseImages();
      this._renderHero();
      this._renderControls();

      // Re-fit the hero box on resize (available space changes independently
      // of any state change, so this bypasses the full _renderHero rebuild).
      let resizeRaf = null;
      window.addEventListener('resize', () => {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(() => {
          resizeRaf = null;
          this._fitHeroBox();
        });
      });
    }

    _preloadBaseImages() {
      const { steps } = this.config;
      for (const p of steps.step1 || []) {
        if (p.baseImage) {
          const img = new Image();
          img.src = p.baseImage;
        }
      }
    }

    // ─── Front / tassel gating ─────────────────────────────────────────────────

    // The product acting as the front layer (step-2 selection, else matching deco), or null
    _activeFront() {
      if (this.state.selected.step2) return this.state.selected.step2;
      if (this.state.addDecoStrap) return this.state.selected.step1; // matching deco
      return null;
    }

    // 'ok' | 'no-front' | 'incompatible' — a tassel needs a tassel-compatible front
    _tasselStatus() {
      const front = this._activeFront();
      if (!front) return 'no-front';
      return front.tasselCompatible ? 'ok' : 'incompatible';
    }

    // Drop the tassel if the current front can no longer support one
    _reconcileTassel() {
      if (this.state.selected.step3 && this._tasselStatus() !== 'ok') {
        this.state.selected.step3 = null;
      }
    }

    // Drop any add-on that doesn't fit the current bag size — e.g. after switching
    // between a Classic and Mini bag at step 1 (future multi-bag case).
    _reconcileCompatibility() {
      const bagCompat = this.state.selected.step1?.compatible || '';
      if (!bagCompat) return;
      for (const key of ['step2', 'step3', 'step4']) {
        const sel = this.state.selected[key];
        if (sel && !isCompatible(sel, bagCompat)) {
          this.state.selected[key] = null;
        }
      }
    }

    // ─── Pricing & running discount ────────────────────────────────────────────

    _money(cents) {
      return formatMoney(cents, this.config.moneyFormat);
    }

    // Bag + paid add-ons (steps 2–4). The free matching deco/strap never count.
    // Discount mode: the discount rate rises with the number of add-ons (see
    // DISCOUNT_TIERS). Gift mode: no price discount — instead, add-on count
    // cumulatively unlocks free gift products (see GIFT_TIER_THRESHOLDS).
    _pricing() {
      const { selected } = this.state;
      const bagPrice = selected.step1 ? Number(selected.step1.price) || 0 : 0;
      const addons = ['step2', 'step3', 'step4'].map((k) => selected[k]).filter(Boolean);
      const addonCount = addons.length;
      const subtotal = bagPrice + addons.reduce((sum, p) => sum + (Number(p.price) || 0), 0);

      if (this.isNoReward) {
        return {
          bagPrice,
          addonCount,
          subtotal,
          rate: 0,
          discount: 0,
          total: subtotal,
          unlockedGiftProducts: [],
          unlockedGiftRrpTotal: 0,
        };
      }

      if (this.isGiftMode) {
        const giftTierStatus = this._giftTierStatus(addonCount);
        const unlockedGiftProducts = [];
        const seenIds = new Set();
        for (const tier of giftTierStatus) {
          if (!tier.unlocked) continue;
          for (const gift of tier.products) {
            if (seenIds.has(gift.id)) continue;
            seenIds.add(gift.id);
            unlockedGiftProducts.push(gift);
          }
        }
        const unlockedGiftRrpTotal = unlockedGiftProducts.reduce((sum, g) => sum + (Number(g.rrp) || 0), 0);
        return {
          bagPrice,
          addonCount,
          subtotal,
          rate: 0,
          discount: 0,
          total: subtotal,
          unlockedGiftProducts,
          unlockedGiftRrpTotal,
          giftTierStatus,
        };
      }

      const rate = DISCOUNT_TIERS[Math.min(addonCount, DISCOUNT_TIERS.length - 1)];
      const discount = Math.round(subtotal * rate);
      return {
        bagPrice,
        addonCount,
        subtotal,
        rate,
        discount,
        total: subtotal - discount,
        unlockedGiftProducts: [],
        unlockedGiftRrpTotal: 0,
      };
    }

    // ─── Hero ─────────────────────────────────────────────────────────────────

    _renderHero() {
      this._reconcileCompatibility();
      this._reconcileTassel();

      const { selected, addDecoStrap } = this.state;
      const zoom = Number(this.config.zoom) || 100;
      const zoomScale = zoom / 100;

      // Reuse the wrapper to avoid destroying the base <img> on every call
      let wrapper = this.heroEl.querySelector('.sh-visualiser-hero');
      if (!wrapper) {
        wrapper = el('div', { class: 'sh-visualiser-hero color-scheme-1' });
        this.heroEl.append(wrapper);
      }

      // Inner canvas — all layers live here so zoom scales them together
      let canvas = wrapper.querySelector('.sh-visualiser-hero__canvas');
      if (!canvas) {
        canvas = el('div', { class: 'sh-visualiser-hero__canvas' });
        wrapper.append(canvas);
      }
      canvas.style.transform = zoomScale !== 1 ? `scale(${zoomScale})` : '';
      canvas.style.transformOrigin = 'bottom center';

      // Update base image src without recreating the element (prevents flash)
      const base = selected.step1;
      let baseImg = canvas.querySelector('.sh-visualiser-hero__base');
      if (!baseImg) {
        baseImg = el('img', { class: 'sh-visualiser-hero__base', loading: 'eager' });
        canvas.prepend(baseImg);
      }
      if (base && base.baseImage) {
        if (baseImg.getAttribute('src') !== base.baseImage) {
          baseImg.setAttribute('src', base.baseImage);
          baseImg.setAttribute('alt', base.title || '');
        }
        baseImg.style.display = '';
      } else {
        baseImg.style.display = 'none';
      }

      // Wrapper's box must match the base image's own ratio exactly — the
      // overlay layers below are positioned by percentage against this box,
      // so any letterbox/crop mismatch would throw their alignment off.
      this._currentBase = base;
      this._fitHeroBox();

      // Remove old overlays only — base image is preserved above
      canvas.querySelectorAll('.sh-visualiser-overlay').forEach((o) => o.remove());

      const baseWidth = Number(base?.baseWidth) || 0;
      const s4 = selected.step4;

      // Layer order (bottom → top): base → front/deco → tassel → charm → strap
      // The strap always sits on top (highest z-index).

      // Front / deco — step-2 selection replaces the step-1 matching deco
      if (selected.step2 && selected.step2.overlayImage) {
        const f = selected.step2;
        const frontPos = parsePosition(f.position);
        if (frontPos) {
          canvas.append(this._overlayEl('front', f.overlayImage, f.overlayWidth, frontPos, baseWidth, f.title));
        } else {
          this._warnMissingPosition(f);
        }
      } else if (addDecoStrap && base?.decoImage) {
        canvas.append(
          this._overlayEl('front', base.decoImage, base.decoWidth, MATCHING_OVERLAY_POS, baseWidth, 'Matching deco')
        );
      }

      // Tassel
      if (selected.step3 && selected.step3.overlayImage) {
        const t = selected.step3;
        const tasselPos = parsePosition(t.position);
        if (tasselPos) {
          canvas.append(this._overlayEl('tassel', t.overlayImage, t.overlayWidth, tasselPos, baseWidth, t.title));
        } else {
          this._warnMissingPosition(t);
        }
      }

      // Charm — only when step-4 selection is a charm (not a strap)
      // Always kept behind the step-1 base image, regardless of DOM order.
      if (s4 && !isStrap(s4) && s4.overlayImage) {
        const charmPos = parsePosition(s4.position);
        if (charmPos) {
          canvas.append(this._overlayEl('charm', s4.overlayImage, s4.overlayWidth, charmPos, baseWidth, s4.title, true));
        } else {
          this._warnMissingPosition(s4);
        }
      }

      // Strap — always topmost among step-4 layers, but still behind the
      // step-1 base image. Step-4 strap replaces the step-1 matching strap.
      if (s4 && isStrap(s4) && s4.overlayImage) {
        const strapPos = parsePosition(s4.position);
        if (strapPos) {
          canvas.append(this._overlayEl('strap', s4.overlayImage, s4.overlayWidth, strapPos, baseWidth, s4.title, true));
        } else {
          this._warnMissingPosition(s4);
        }
      } else if (addDecoStrap && base?.strapImage) {
        canvas.append(
          this._overlayEl('strap', base.strapImage, base.strapWidth, MATCHING_OVERLAY_POS, baseWidth, 'Matching strap')
        );
      }
    }

    // Sizes the wrapper (.sh-visualiser-hero) to the largest box that fits
    // within the available space while matching the base image's own aspect
    // ratio exactly — computed in JS, rather than via CSS aspect-ratio/
    // max-width tricks, so the result is never ambiguous or dependent on how
    // the flex/grid auto-sizing algorithms interact.
    //
    // this.heroEl has no fixed height of its own (it must stay auto-sized to
    // this fitted content, or it'd reintroduce empty space on tall screens);
    // its CSS only caps how tall it's ALLOWED to get via max-height. That cap
    // is what we need here, not clientHeight — clientHeight would just be
    // this same wrapper's height reflected back, which is circular.
    _fitHeroBox() {
      const wrapper = this.heroEl.querySelector('.sh-visualiser-hero');
      const base = this._currentBase;
      if (!wrapper || !base || !base.baseWidth || !base.baseHeight) return;

      const availW = this.heroEl.clientWidth;
      const maxHeight = parseFloat(getComputedStyle(this.heroEl).maxHeight);
      const availH = Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : window.innerHeight;
      if (availW <= 0 || availH <= 0) return;

      const ratio = base.baseWidth / base.baseHeight;
      let w = availW;
      let h = w / ratio;
      if (h > availH) {
        h = availH;
        w = h * ratio;
      }

      wrapper.style.width = `${w}px`;
      wrapper.style.height = `${h}px`;

      // Cap the controls column at the image's own fitted height (desktop
      // only — mobile stacks the columns, so nothing to cap there).
      if (window.matchMedia('(min-width: 990px)').matches) {
        this.controlsEl.style.maxHeight = `${h}px`;
      } else {
        this.controlsEl.style.maxHeight = '';
      }
    }

    // A selected step-2/3/4 product has an overlay image but no usable position
    // for this visualiser — it silently won't be drawn on the hero image. This
    // usually means the visualiser (or the bag it's attached to) was renamed in
    // Shopify admin, changing its handle and breaking the per-bag position field
    // key. Logged so the cause is obvious in devtools instead of a customer just
    // noticing something's missing.
    _warnMissingPosition(product) {
      console.warn(
        `[ProductVisualiser] "${product.title}" has no overlay position for this visualiser. ` +
          `Expected a "${this.config.posFieldKey}" value on its style metaobject — check the ` +
          `visualiser or bag wasn't renamed (which changes its handle).`
      );
    }

    _overlayEl(name, imageUrl, intrinsicWidth, pos, baseWidth, alt, behindBase = false) {
      const div = el('div', { class: `sh-visualiser-overlay sh-visualiser-overlay--${name}` });

      const jsonOverlayWidth = Number(intrinsicWidth) || 0;

      const applyWidth = (overlayWidth) => {
        if (baseWidth > 0 && overlayWidth > 0) {
          const intrinsicWidthPercent = (overlayWidth / baseWidth) * 100;
          div.style.width = `${intrinsicWidthPercent * (pos.scale / 100)}%`;
        } else {
          div.style.width = `${pos.scale}%`;
        }
      };

      // Position the container; width applied below. Layer stack (back to
      // front): step-4 charm/strap (0) → step-1 base image (1, set in CSS)
      // → front/deco + tassel (2). Step-4 layers stay behind the base image
      // no matter their order in the DOM.
      const zIndex = behindBase ? 0 : 2;
      div.style.cssText = `position:absolute;top:${pos.top};left:${pos.left};transform:translateX(-50%);overflow:hidden;z-index:${zIndex};`;

      const img = el('img', { src: imageUrl, alt: alt || '' });
      img.style.cssText = 'display:block;width:100%;height:auto;';
      div.append(img);

      if (jsonOverlayWidth > 0) {
        // Dimensions known from JSON — apply immediately
        applyWidth(jsonOverlayWidth);
      } else {
        // Dimensions missing from JSON — measure naturalWidth after load
        // Keep width:0 until load to prevent a full-width flash
        div.style.width = '0';
        const onLoad = () => {
          if (img.naturalWidth > 0) {
            applyWidth(img.naturalWidth);
          } else {
            div.style.width = `${pos.scale}%`;
          }
        };
        if (img.complete && img.naturalWidth > 0) {
          onLoad();
        } else {
          img.addEventListener('load', onLoad, { once: true });
        }
      }

      return div;
    }

    // ─── Controls ─────────────────────────────────────────────────────────────

    _renderControls() {
      this.controlsEl.innerHTML = '';
      const { steps } = this.config;
      const nodes = [
        this._renderStep1(),
        this._renderLookStep(2, 'step2', 'Choose a Style Pop-On®'),
        this._renderLookStep(3, 'step3', 'Choose a Style Pop-On®'),
      ];
      // Hide step 4 entirely when the metaobject has no products configured for it.
      if ((steps.step4 || []).length > 0) {
        nodes.push(this._renderLookStep(4, 'step4', 'Choose a Charm'));
      }
      this.controlsEl.append(...nodes);
      if (!this.isBasic) {
        this.controlsEl.append(this._renderAddToCart());
      }
    }

    // Wraps step content in a header + always-open body (steps stay expanded)
    _wrapAccordion(stepNum, title, contentNodes) {
      const wrap = el('div', {
        class: `sh-visualiser-step sh-visualiser-step--${stepNum} is-open`,
      });

      const toggle = el(
        'div',
        { class: 'sh-visualiser-step__toggle' },
        el('span', { class: 'sh-visualiser-step__num' }, `${stepNum}`),
        el('span', { class: 'sh-visualiser-step__title' }, title.toUpperCase())
      );

      const body = el('div', { class: 'sh-visualiser-step__body' });
      contentNodes.forEach((n) => body.append(n));

      wrap.append(toggle, body);
      return wrap;
    }

    _renderStep1() {
      const { steps } = this.config;
      const { hardware, selected } = this.state;
      const filtered = filterByHardware(steps.step1 || [], hardware);

      const content = [];

      content.push(labelRow('Hardware Colour', hardware.toUpperCase()));
      const toggleWrap = el('div', { class: 'sh-visualiser-hardware-toggle' });
      for (const hw of ['Gold', 'Silver']) {
        const btn = el(
          'button',
          { class: 'sh-visualiser-pill-btn sh-visualiser-hardware-btn', type: 'button' },
          hw.toUpperCase()
        );
        btn.setAttribute('aria-pressed', hw === hardware ? 'true' : 'false');
        if (hw === hardware) btn.classList.add('is-active');
        btn.addEventListener('click', () => this._setHardware(hw));
        toggleWrap.append(btn);
      }
      content.push(toggleWrap);

      // FUTURE: product-style selector (Mini S / Dahlia / Sarah Tote) sits here,
      // under Hardware Colour, once more bag styles launch.

      if (filtered.length === 0) {
        content.push(el('p', { class: 'sh-visualiser-unavailable' }, `Not available in ${hardware}`));
        return this._wrapAccordion(1, 'Choose Bag Colour', content);
      }

      const activeColour = selected.step1?.colour?.label || '';
      content.push(labelRow('Bag Colour', activeColour.toUpperCase()));
      content.push(
        this._swatchRow(filtered, selected.step1, (prod) => {
          this.state.selected.step1 = prod;
          this._renderHero();
          this._renderControls();
        })
      );

      return this._wrapAccordion(1, 'Choose Bag Colour', content);
    }

    // Shared renderer for steps 2–4: look filter row + colour swatches
    _renderLookStep(stepNum, stepKey, title) {
      const { steps } = this.config;
      const { hardware } = this.state;
      // Only show accessories that fit the current bag size (Classic / Mini / Universal)
      const bagCompat = this.state.selected.step1?.compatible || '';
      const filtered = filterByHardware(steps[stepKey] || [], hardware).filter((p) =>
        isCompatible(p, bagCompat)
      );

      const content = [];

      // Step 3 (tassel) is gated by the front layer
      if (stepKey === 'step3') {
        const status = this._tasselStatus();
        if (status !== 'ok') {
          const msg =
            status === 'no-front'
              ? 'Choose a Bag Colour (Step 1) or add a Deco (Step 2) before choosing a tassel.'
              : 'This front doesn’t have space for a tassel.';
          content.push(el('p', { class: 'sh-visualiser-warning' }, msg));
          return this._wrapAccordion(stepNum, title, content);
        }
      }

      if (filtered.length === 0) {
        content.push(el('p', { class: 'sh-visualiser-unavailable' }, `Not available in ${hardware}`));
        return this._wrapAccordion(stepNum, title, content);
      }

      let lookProducts;
      if (this.isBasic || !this.chooseLookEnabled) {
        // Basic mode, or Choose Look switched off on the metaobject, has no
        // look concept — pool every compatible product together.
        lookProducts = filtered;
      } else {
        const currentLook = this.state.look[stepKey];
        const currentLookLabel = (LOOKS.find(([v]) => v === currentLook) || [, ''])[1];

        content.push(labelRow('Choose a look', currentLookLabel.toUpperCase()));
        const filterRow = el('div', { class: 'sh-visualiser-look-filter' });
        for (const [val, lbl] of LOOKS) {
          const btn = el(
            'button',
            { class: 'sh-visualiser-pill-btn sh-visualiser-look-btn', type: 'button' },
            lbl.toUpperCase()
          );
          btn.setAttribute('aria-pressed', val === currentLook ? 'true' : 'false');
          if (val === currentLook) btn.classList.add('is-active');
          btn.addEventListener('click', () => {
            this.state.look[stepKey] = val;
            this.state.style[stepKey] = null; // re-derived below for the new look
            this._renderControls();
          });
          filterRow.append(btn);
        }
        content.push(filterRow);

        lookProducts = filterByLook(filtered, currentLook);
        if (lookProducts.length === 0) {
          content.push(el('p', { class: 'sh-visualiser-unavailable' }, 'Nothing in this look yet.'));
          return this._wrapAccordion(stepNum, title, content);
        }
      }

      // Style/product filter — narrows the look down to one product name,
      // colour swatches below then only show that product's own colours.
      const styleGroups = groupByStyle(lookProducts);
      const styleNames = [...styleGroups.keys()];

      let currentStyle = this.state.style[stepKey];
      if (!currentStyle || !styleGroups.has(currentStyle)) {
        currentStyle = styleNames[0] || null;
        this.state.style[stepKey] = currentStyle;
      }

      content.push(labelRow('Style', (currentStyle || '').toUpperCase()));

      const styleRow = el('div', { class: 'sh-visualiser-swatches sh-visualiser-style-filter' });
      for (const [name, groupProducts] of styleGroups) {
        const thumb = groupProducts[0];
        const isActive = name === currentStyle;
        const btn = el('button', {
          class: 'sh-visualiser-swatch sh-visualiser-style-btn',
          type: 'button',
          title: name,
        });
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) btn.classList.add('is-active');

        const colour = thumb.colour || {};
        if (thumb.productImage) {
          btn.append(el('img', { src: thumb.productImage, alt: name, loading: 'lazy' }));
        } else if (colour.image) {
          btn.append(el('img', { src: colour.image, alt: name, loading: 'lazy' }));
        } else if (colour.hex && colour.hex !== '#cccccc') {
          btn.style.backgroundColor = colour.hex;
        }

        btn.addEventListener('click', () => {
          this.state.style[stepKey] = name;
          // Drop the current colour selection if it belongs to a different style
          const cur = this.state.selected[stepKey];
          if (cur && cur.title !== name) this.state.selected[stepKey] = null;
          this._renderHero();
          this._renderControls();
        });
        styleRow.append(btn);
      }
      content.push(styleRow);

      const styleProducts = styleGroups.get(currentStyle) || [];

      const selected = this.state.selected[stepKey];
      const colourLabel = selected ? selected.colour?.label || selected.title : NONE_LABELS[stepKey];
      content.push(labelRow('Colour', colourLabel.toUpperCase()));
      content.push(
        this._swatchRow(
          styleProducts,
          selected,
          (prod) => {
            this.state.selected[stepKey] = prod;
            this._renderHero();
            this._renderControls();
          },
          { allowNone: true }
        )
      );

      return this._wrapAccordion(stepNum, title, content);
    }

    _renderAddToCart() {
      const wrap = el('div', { class: 'sh-visualiser-cart-wrap' });
      const pricing = this._pricing();

      // Total (left) + add button (right)
      const totalWrap = el('div', { class: 'sh-visualiser-total' });
      if (pricing.discount > 0) {
        totalWrap.append(el('span', { class: 'sh-visualiser-total__was' }, this._money(pricing.subtotal)));
      }
      totalWrap.append(el('span', { class: 'sh-visualiser-total__value' }, this._money(pricing.total)));

      this.addBtn = el('button', { class: 'sh-visualiser-add-btn button', type: 'button' }, 'Add all to basket');
      if (!this.state.selected.step1) {
        this.addBtn.setAttribute('aria-disabled', 'true');
      }
      this.addBtn.addEventListener('click', () => this._addToCart());

      const row = el('div', { class: 'sh-visualiser-cart-row' }, totalWrap, this.addBtn);

      this.errorEl = el('p', { class: 'sh-visualiser-cart-error hidden' });

      wrap.append(row);
      if (!this.isNoReward) {
        const msgEl = this.isGiftMode ? this._renderGiftMsg(pricing) : this._renderDiscountMsg(pricing);
        wrap.append(msgEl);
      }
      wrap.append(this.errorEl);
      return wrap;
    }

    // Running-discount line: what's unlocked + a CTA to the next tier
    _renderDiscountMsg(pricing) {
      const { addonCount, rate } = pricing;
      const maxIndex = DISCOUNT_TIERS.length - 1;
      const pct = (r) => `${Math.round(r * 100)}%`;
      const msg = el('p', { class: 'sh-visualiser-discount' });

      if (addonCount === 0) {
        msg.append(`Add 1 item for ${pct(DISCOUNT_TIERS[1])} off your order`);
      } else {
        msg.append(el('span', { class: 'sh-visualiser-discount__unlocked' }, `${pct(rate)} discount unlocked`));
        if (addonCount < maxIndex) {
          msg.append(` | Add 1 more item for ${pct(DISCOUNT_TIERS[addonCount + 1])} off`);
        }
      }
      return msg;
    }

    // Resolves each configured gift tier against the current bag hardware once
    // (in-stock, hardware-matching products + whether addonCount has unlocked
    // it) — shared by _pricing() (cart line items) and _renderGiftMsg()
    // (progress copy), so the copy never promises a gift that won't be carted.
    _giftTierStatus(addonCount) {
      const giftTiers = this.config.giftTiers || [];
      return giftTiers.map((tier, i) => {
        const threshold = GIFT_TIER_THRESHOLDS[i];
        const products = filterByHardware(tier.products || [], this.state.hardware);
        const unlocked = threshold != null && addonCount >= threshold;
        if (unlocked && (tier.products || []).length && products.length === 0) {
          console.warn(
            `[ProductVisualiser] No available ${this.state.hardware} gift product configured for tier ${tier.tier}.`
          );
        }
        return { tier: tier.tier, threshold, products, unlocked };
      });
    }

    // Free-gift line: what's unlocked + a CTA to the next tier, mirroring the
    // running-discount message but for cumulative free products instead of %.
    _renderGiftMsg(pricing) {
      const { addonCount, giftTierStatus } = pricing;
      const giftTiers = this.config.giftTiers || [];
      const tier1 = giftTierStatus[0]?.products || [];
      const tier2 = giftTierStatus[1]?.products || [];
      const msg = el('p', { class: 'sh-visualiser-discount' });

      if (giftTiers.length === 0 || (tier1.length === 0 && tier2.length === 0)) {
        console.warn('[ProductVisualiser] Gift mode enabled but no gift tier products are configured/available.');
        return msg;
      }

      const titles = (gifts) => gifts.map((g) => g.title).join(' & ');
      const rrp = (gifts) => this._money(gifts.reduce((sum, g) => sum + (Number(g.rrp) || 0), 0));

      if (addonCount === 0) {
        if (tier1.length) {
          msg.append(`Add 1 accessory to get a FREE ${titles(tier1)} (${rrp(tier1)} RRP)`);
        }
      } else if (addonCount === 1) {
        if (tier1.length) {
          msg.append(el('span', { class: 'sh-visualiser-discount__unlocked' }, `FREE ${titles(tier1)} unlocked`));
        }
        if (tier2.length) {
          const combined = rrp([...tier1, ...tier2]);
          msg.append(` | Add 1 more accessory to also get a FREE ${titles(tier2)} (${combined} RRP)`);
        }
      } else {
        const all = [...tier1, ...tier2];
        if (all.length) {
          msg.append(
            el(
              'span',
              { class: 'sh-visualiser-discount__unlocked' },
              `FREE ${titles(all)} unlocked (${rrp(all)} RRP)`
            )
          );
        }
      }

      return msg;
    }

    // ─── Swatch helpers ───────────────────────────────────────────────────────

    // `allowNone` steps have no dedicated "remove" swatch — clicking the
    // already-selected swatch deselects it instead (see _swatchBtn's close icon).
    _swatchRow(products, selected, onSelect, { allowNone = false } = {}) {
      const row = el('div', { class: 'sh-visualiser-swatches' });
      for (const prod of products) {
        const isSelected = !!(selected && prod.id === selected.id);
        const removable = allowNone && isSelected;
        row.append(
          this._swatchBtn(prod, isSelected, () => onSelect(removable ? null : prod), { removable })
        );
      }
      return row;
    }

    // Product swatch — square, uses the product's own photo where available,
    // falling back to the colour swatch image, then a hex/hardware colour.
    // When `removable`, the selected swatch carries a small close icon in its
    // corner and clicking it deselects rather than re-selecting.
    _swatchBtn(prod, isSelected, onClick, { removable = false } = {}) {
      const colour = prod.colour || {};
      const label = colour.label || prod.title;
      const btn = el('button', {
        class: 'sh-visualiser-swatch',
        type: 'button',
        title: removable ? `Remove ${label}` : label,
      });
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      if (isSelected) btn.classList.add('is-active');
      if (removable) {
        btn.classList.add('sh-visualiser-swatch--removable');
        btn.setAttribute('aria-label', `Remove ${label}`);
        btn.append(el('span', { class: 'sh-visualiser-swatch__remove', 'aria-hidden': 'true' }));
      }

      if (prod.productImage) {
        btn.append(el('img', { src: prod.productImage, alt: colour.label || prod.title, loading: 'lazy' }));
      } else if (colour.label && colour.image) {
        btn.append(el('img', { src: colour.image, alt: colour.label, loading: 'lazy' }));
      } else if (colour.label && colour.hex && colour.hex !== '#cccccc') {
        btn.style.backgroundColor = colour.hex;
      } else {
        const hwHex = { Gold: '#D7A747', Silver: '#C4C4C4' }[prod.hardware];
        btn.style.backgroundColor = hwHex || colour.hex || '#cccccc';
      }

      btn.addEventListener('click', onClick);
      return btn;
    }

    // ─── Hardware toggle ──────────────────────────────────────────────────────

    _setHardware(hw) {
      if (this.state.hardware === hw) return;
      this.state.hardware = hw;

      const { steps } = this.config;

      // Keep the same bag colour where available, else fall back to the first
      const s1 = filterByHardware(steps.step1 || [], hw);
      const prevLabel = this.state.selected.step1?.colour?.label;
      this.state.selected.step1 = s1.find((p) => p.colour?.label === prevLabel) || s1[0] || null;

      // Overlay selections may not exist in the new hardware — reset them
      this.state.selected.step2 = null;
      this.state.selected.step3 = null;
      this.state.selected.step4 = null;
      this.state.look = { step2: 'day', step3: 'day', step4: 'day' };
      this.state.style = { step2: null, step3: null, step4: null };

      this._renderHero();
      this._renderControls();
    }

    // ─── Add to cart ──────────────────────────────────────────────────────────

    _buildLineItems() {
      const { selected } = this.state;
      const items = [];

      // Matching deco & strap are visual-only (free with the bag) — never carted.
      for (const key of ['step1', 'step2', 'step3', 'step4']) {
        const prod = selected[key];
        if (prod) items.push({ id: prod.variantId, title: prod.title });
      }

      if (this.isGiftMode) {
        // _pricing() already resolves unlockedGiftProducts to the current bag
        // hardware and filters out unavailable products (see filterByHardware).
        const selectedVariantIds = new Set(items.map((i) => i.id));
        const { unlockedGiftProducts } = this._pricing();
        for (const gift of unlockedGiftProducts) {
          // Already in the cart as a deliberate paid selection — don't also gift it.
          if (selectedVariantIds.has(gift.variantId)) continue;
          items.push({ id: gift.variantId, title: gift.title, properties: { _visualiser_gift: 'true' } });
        }
      }

      return items;
    }

    async _addToCart() {
      if (this.addBtn.getAttribute('aria-disabled') === 'true') return;

      const items = this._buildLineItems();
      if (items.length === 0) return;

      this._setCartLoading(true);
      this._showError('');

      try {
        // Add all items in a single request
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items: items.map((item) => ({
            id: item.id,
            quantity: 1,
            ...(item.properties ? { properties: item.properties } : {}),
          })),
        });
        const response = await fetch(routes.cart_add_url, config);
        if (!response.ok && response.status !== 422) {
          throw new Error(`Unexpected server response (${response.status}). Please try again.`);
        }
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message || 'Could not add to cart.');

        try {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-visualiser' });
        } catch (pubErr) {
          console.warn('[ProductVisualiser] Cart update publish error', pubErr);
        }

        if (this.cart && this.cart.tagName === 'CART-DRAWER') {
          // Fetch rendered sections via GET (after all items are in the cart)
          try {
            const sectionIds = this.cart.getSectionsToRender().map((s) => s.id).join(',');
            const sectResp = await fetch(`${window.location.pathname}?sections=${sectionIds}`);
            if (!sectResp.ok) throw new Error('sections fetch failed');
            const sections = await sectResp.json();
            this.cart.renderContents({ sections });
          } catch {
            window.location = window.routes.cart_url;
          }
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (e) {
        console.error('[ProductVisualiser] Add to cart error', e);
        this._showError(e.message || 'Something went wrong. Please try again.');
      } finally {
        this._setCartLoading(false);
      }
    }

    _setCartLoading(loading) {
      if (loading) {
        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.classList.add('loading');
      } else {
        if (this.state.selected.step1) {
          this.addBtn.removeAttribute('aria-disabled');
        }
        this.addBtn.classList.remove('loading');
      }
    }

    _showError(msg) {
      if (!this.errorEl) return;
      if (msg) {
        this.errorEl.textContent = msg;
        this.errorEl.classList.remove('hidden');
      } else {
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }
    }
  }

  // ─── Boot ────────────────────────────────────────────────────────────────────

  document.querySelectorAll('[data-product-visualiser]').forEach((root) => {
    new ProductVisualiser(root);
  });
})();
